import React, { useState, useEffect } from 'react';
import { Table, Pagination, Button, Spinner, Toast } from 'react-bootstrap';
import { FileText, Trash2, Download, FileArchive, AlertCircle } from 'lucide-react';

const ROWS_PER_PAGE = 15;

/**
 * Tabla paginada de resultados de facturas CFDI.
 *
 * Props:
 *   - facturas         : array de comprobantes
 *   - onDownloadXml     : async (factura) => void  — descarga XML lazy por UUID
 *   - onDownloadPdf     : async (factura) => void  — descarga PDF lazy por UUID
 *   - onDownloadAllXml  : async () => void          — descarga el XML de todas las facturas en un .zip
 *   - onDownloadAllPdf  : async () => void          — descarga el PDF de todas las facturas en un .zip
 *   - onClear           : () => void
 *
 * Campos del response (verificados contra respuesta real):
 *   uuid, rfCemisor (capital C), razonSocialEmisor, rfcReceptor,
 *   razonSocialReceptor, fechaEmision, total (string "$32,327.00"),
 *   estadoDeComprobante, efectoDelComprobante, fileName
 */
const FacturaResultados = ({ facturas, onDownloadXml, onDownloadPdf, onDownloadAllXml, onDownloadAllPdf, onClear }) => {
  const [currentPage, setCurrentPage]         = useState(1);
  const [downloadingXmlUuids, setDownloadingXmlUuids] = useState(new Set());
  const [downloadingPdfUuids, setDownloadingPdfUuids] = useState(new Set());
  const [downloadingAllXml, setDownloadingAllXml] = useState(false);
  const [downloadingAllPdf, setDownloadingAllPdf] = useState(false);
  const [progressAllXml, setProgressAllXml]       = useState(null); // { done, total }
  const [progressAllPdf, setProgressAllPdf]       = useState(null);
  const [errorToast, setErrorToast]           = useState('');
  const [showErrorToast, setShowErrorToast]   = useState(false);

  // Reset a página 1 cuando cambian los resultados
  useEffect(() => { setCurrentPage(1); }, [facturas]);

  const totalPages = Math.max(1, Math.ceil(facturas.length / ROWS_PER_PAGE));
  const startIdx   = (currentPage - 1) * ROWS_PER_PAGE;
  const pageRows   = facturas.slice(startIdx, startIdx + ROWS_PER_PAGE);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const truncateUuid = (uuid) => {
    if (!uuid) return '—';
    return uuid.length <= 14 ? uuid : `${uuid.substring(0, 8)}…${uuid.substring(uuid.length - 4)}`;
  };

  const getEstadoBadge = (estado) => {
    const s = (estado || '').toLowerCase();
    if (s === 'vigente')      return <span className="status-badge status-active">● Vigente</span>;
    if (s.startsWith('cancel')) return <span className="status-badge status-cancelled">✕ Cancelada</span>;
    return <span className="status-badge" style={{ background: '#f0f0f0', color: '#888' }}>{estado || '—'}</span>;
  };

  const makeDownloadHandler = (setLoading, fn, fallbackMsg) => async (f) => {
    setLoading(prev => new Set([...prev, f.uuid]));
    try {
      await fn(f);
    } catch (err) {
      setErrorToast(err.message || fallbackMsg);
      setShowErrorToast(true);
    } finally {
      setLoading(prev => { const s = new Set(prev); s.delete(f.uuid); return s; });
    }
  };

  const handleDownloadXmlClick = makeDownloadHandler(
    setDownloadingXmlUuids, onDownloadXml, 'No se pudo descargar el XML.'
  );
  const handleDownloadPdfClick = makeDownloadHandler(
    setDownloadingPdfUuids, onDownloadPdf, 'No se pudo descargar el PDF.'
  );

  const makeDownloadAllHandler = (setLoading, setProgress, fn, fallbackMsg) => async () => {
    setLoading(true);
    setProgress({ done: 0, total: facturas.length });
    try {
      await fn((done, total) => setProgress({ done, total }));
    } catch (err) {
      setErrorToast(err.message || fallbackMsg);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleDownloadAllXmlClick = makeDownloadAllHandler(
    setDownloadingAllXml, setProgressAllXml, onDownloadAllXml, 'No se pudo generar el ZIP de XML.'
  );
  const handleDownloadAllPdfClick = makeDownloadAllHandler(
    setDownloadingAllPdf, setProgressAllPdf, onDownloadAllPdf, 'No se pudo generar el ZIP de PDF.'
  );

  // Paginación
  const paginationItems = [];
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage   = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
  for (let p = startPage; p <= endPage; p++) {
    paginationItems.push(
      <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>
        {p}
      </Pagination.Item>
    );
  }

  return (
    <div className="fade-in-up mt-4">

      {/* Toast de error al descargar XML */}
      <Toast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        delay={5000}
        autohide
        className="custom-toast toast-danger"
      >
        <Toast.Header>
          <AlertCircle size={16} className="me-2 text-danger" />
          <strong className="me-auto">Error al descargar</strong>
        </Toast.Header>
        <Toast.Body style={{ fontSize: '.87rem' }}>{errorToast}</Toast.Body>
      </Toast>

      {/* Meta row */}
      <div className="results-meta">
        <span className="results-count">
          <FileText size={16} className="me-2 text-primary" />
          {facturas.length} factura{facturas.length !== 1 ? 's' : ''} encontrada{facturas.length !== 1 ? 's' : ''}
        </span>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={handleDownloadAllXmlClick}
            disabled={downloadingAllXml || facturas.length === 0}
            className="d-flex align-items-center gap-1">
            {downloadingAllXml
              ? <><Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                  Descargando {progressAllXml?.done ?? 0} de {progressAllXml?.total ?? facturas.length}…</>
              : <><FileArchive size={14} />Descargar todos XML</>
            }
          </Button>
          <Button variant="outline-danger" size="sm" onClick={handleDownloadAllPdfClick}
            disabled={downloadingAllPdf || facturas.length === 0}
            className="d-flex align-items-center gap-1">
            {downloadingAllPdf
              ? <><Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                  Descargando {progressAllPdf?.done ?? 0} de {progressAllPdf?.total ?? facturas.length}…</>
              : <><FileArchive size={14} />Descargar todos PDF</>
            }
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onClear}
            className="d-flex align-items-center gap-1">
            <Trash2 size={14} />
            Limpiar
          </Button>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-responsive">
        <Table className="modern-table" hover>
          <thead>
            <tr>
              <th>UUID</th>
              <th>Fecha emisión</th>
              <th>RFC Emisor</th>
              <th>Razón social emisor</th>
              <th>RFC Receptor</th>
              <th>Total</th>
              <th>Efecto</th>
              <th>Estado</th>
              <th>Descargas</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">Sin resultados.</td>
              </tr>
            ) : (
              pageRows.map((f, idx) => {
                const xmlLoading = downloadingXmlUuids.has(f.uuid);
                const pdfLoading = downloadingPdfUuids.has(f.uuid);
                return (
                  <tr key={f.uuid || idx}>

                    {/* UUID */}
                    <td>
                      <span className="uuid-cell" title={f.uuid || ''} style={{ cursor: 'help' }}>
                        {truncateUuid(f.uuid)}
                      </span>
                    </td>

                    {/* Fecha — campo: fechaEmision */}
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(f.fechaEmision)}</td>

                    {/* RFC Emisor — campo real: rfCemisor (capital C) */}
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>
                      {f.rfCemisor || '—'}
                    </td>

                    {/* Razón social emisor */}
                    <td title={f.razonSocialEmisor || ''}
                      style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.razonSocialEmisor || '—'}
                    </td>

                    {/* RFC Receptor */}
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>
                      {f.rfcReceptor || '—'}
                    </td>

                    {/* Total — viene como string "$32,327.00", mostrar tal cual */}
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{f.total || '—'}</td>

                    {/* Efecto del comprobante */}
                    <td><span style={{ fontSize: '.82rem', color: '#6c757d' }}>{f.efectoDelComprobante || '—'}</span></td>

                    {/* Estado — campo: estadoDeComprobante */}
                    <td>{getEstadoBadge(f.estadoDeComprobante)}</td>

                    {/* Botones XML y PDF — descarga lazy por UUID */}
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownloadXmlClick(f)}
                          disabled={xmlLoading}
                          className="d-flex align-items-center gap-1 py-1"
                          title={`Descargar XML de ${f.uuid || 'este comprobante'}`}
                        >
                          {xmlLoading
                            ? <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                            : <Download size={13} />
                          }
                          XML
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDownloadPdfClick(f)}
                          disabled={pdfLoading}
                          className="d-flex align-items-center gap-1 py-1"
                          title={`Descargar PDF de ${f.uuid || 'este comprobante'}`}
                        >
                          {pdfLoading
                            ? <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                            : <Download size={13} />
                          }
                          PDF
                        </Button>
                      </div>
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <Pagination size="sm">
            <Pagination.Prev
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            />
            {startPage > 1 && <Pagination.Ellipsis disabled />}
            {paginationItems}
            {endPage < totalPages && <Pagination.Ellipsis disabled />}
            <Pagination.Next
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            />
          </Pagination>
          <span className="text-muted small ms-2">Página {currentPage} de {totalPages}</span>
        </div>
      )}
    </div>
  );
};

export default FacturaResultados;
